import React, { useState, useEffect } from 'react';
import logo from '../TBL Logo White.png';
import './BlendingTrials.css';

const BlendingTrials = ({ classVersion, onNext, onBack }) => {
  const [currentSection, setCurrentSection] = useState('intro'); // 'intro', 'guided', 'recommended'
  const [currentTrial, setCurrentTrial] = useState(0); // 0: Wine 1, 1: Wine 2
  const [cylinderSize, setCylinderSize] = useState('100ML');
  const [wineRatings, setWineRatings] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState(null);

  // Load saved data from session storage
  useEffect(() => {
    const savedWineRatings = sessionStorage.getItem('wineCharacteristicRatings');
    const savedQuizAnswers = sessionStorage.getItem('quizAnswers');
    
    console.log('BlendingTrials: Loading saved data:', {
      savedWineRatings,
      savedQuizAnswers
    });
    
    if (savedWineRatings) {
      try {
        const parsedRatings = JSON.parse(savedWineRatings);
        console.log('BlendingTrials: Parsed wine ratings:', parsedRatings);
        setWineRatings(parsedRatings);
      } catch (error) {
        console.error('BlendingTrials: Error parsing wine ratings:', error);
      }
    }
    
    if (savedQuizAnswers) {
      try {
        const parsedAnswers = JSON.parse(savedQuizAnswers);
        console.log('BlendingTrials: Parsed quiz answers:', parsedAnswers);
        setQuizAnswers(parsedAnswers);
      } catch (error) {
        console.error('BlendingTrials: Error parsing quiz answers:', error);
      }
    }
  }, []);

  const guidedTrials = [
    {
      name: 'Wine 1 — 50/50',
      description: '50% Wine 2, 50% Wine 3',
      wine2: 50,
      wine3: 50
    },
    {
      name: 'Wine 2 — 80/20',
      description: '80% Wine 1, 20% Wine 3',
      wine1: 80,
      wine3: 20
    }
  ];

  const cylinderSizes = [
    { value: '100ML', label: '100ML' },
    { value: '50ML', label: '50ML' },
    { value: '25ML', label: '25ML' }
  ];

  const getMLAmount = (percentage, cylinderSize) => {
    const size = parseInt(cylinderSize);
    return Math.round((percentage / 100) * size);
  };

  // Helper function to round percentages to nearest 5% or 10%
  const roundToNearestInterval = (percentage, interval = 5) => {
    return Math.round(percentage / interval) * interval;
  };

  // Helper function to normalize percentages to add up to 100%
  const normalizePercentages = (wine1, wine2, wine3) => {
    const total = wine1 + wine2 + wine3;
    if (total === 100) return { wine1, wine2, wine3 };
    
    // Find the wine with the highest percentage to adjust
    const maxWine = wine1 >= wine2 && wine1 >= wine3 ? 'wine1' :
                   wine2 >= wine3 ? 'wine2' : 'wine3';
    
    const adjustment = 100 - total;
    let finalWine1 = wine1;
    let finalWine2 = wine2;
    let finalWine3 = wine3;
    
    if (maxWine === 'wine1') finalWine1 += adjustment;
    else if (maxWine === 'wine2') finalWine2 += adjustment;
    else finalWine3 += adjustment;
    
    return { wine1: finalWine1, wine2: finalWine2, wine3: finalWine3 };
  };

  // Generate recommended blends based on user preferences and ratings
  const generateRecommendedBlends = () => {
    if (!wineRatings || !quizAnswers) {
      return [];
    }

    // Calculate user preferences from quiz answers
    const calculatePreference = (answers, category) => {
      const categoryAnswers = answers.filter(answer => answer.category === category);
      const values = categoryAnswers.map(answer => answer.value);
      
      const counts = values.reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});
      
      const maxCount = Math.max(...Object.values(counts));
      return Object.keys(counts).find(key => counts[key] === maxCount);
    };

    const acidityPreference = calculatePreference(quizAnswers, 'acidity');
    const sweetnessPreference = calculatePreference(quizAnswers, 'sweetness');
    const bodyPreference = calculatePreference(quizAnswers, 'body');

    console.log('User preferences:', { acidityPreference, sweetnessPreference, bodyPreference });

    // Calculate average ratings for each wine
    const wineAverages = {
      wine1: {
        acidity: (wineRatings.acidity.wine1 || 0),
        sweetness: (wineRatings.sweetness.wine1 || 0),
        body: (wineRatings.body.wine1 || 0)
      },
      wine2: {
        acidity: (wineRatings.acidity.wine2 || 0),
        sweetness: (wineRatings.sweetness.wine2 || 0),
        body: (wineRatings.body.wine2 || 0)
      },
      wine3: {
        acidity: (wineRatings.acidity.wine3 || 0),
        sweetness: (wineRatings.sweetness.wine3 || 0),
        body: (wineRatings.body.wine3 || 0)
      }
    };

    console.log('Wine averages:', wineAverages);

    // Generate blend recommendations based on preferences
    const recommendations = [];

    // Recommendation 1: Favor the wine with highest overall rating
    const wine1Total = wineAverages.wine1.acidity + wineAverages.wine1.sweetness + wineAverages.wine1.body;
    const wine2Total = wineAverages.wine2.acidity + wineAverages.wine2.sweetness + wineAverages.wine2.body;
    const wine3Total = wineAverages.wine3.acidity + wineAverages.wine3.sweetness + wineAverages.wine3.body;

    if (wine1Total >= wine2Total && wine1Total >= wine3Total) {
      recommendations.push({
        name: 'Wine 1 Dominant',
        description: '70% Wine 1, 20% Wine 2, 10% Wine 3',
        wine1: 70,
        wine2: 20,
        wine3: 10
      });
    } else if (wine2Total >= wine3Total) {
      recommendations.push({
        name: 'Wine 2 Dominant',
        description: '20% Wine 1, 70% Wine 2, 10% Wine 3',
        wine1: 20,
        wine2: 70,
        wine3: 10
      });
    } else {
      recommendations.push({
        name: 'Wine 3 Dominant',
        description: '20% Wine 1, 10% Wine 2, 70% Wine 3',
        wine1: 20,
        wine2: 10,
        wine3: 70
      });
    }

    // Recommendation 2: Balanced blend
    recommendations.push({
      name: 'Balanced Blend',
      description: '40% Wine 1, 35% Wine 2, 25% Wine 3',
      wine1: 40,
      wine2: 35,
      wine3: 25
    });

    // Recommendation 3: Based on acidity preference - properly weighted by user ratings
    if (acidityPreference === 'high') {
      const totalAcidityRating = wineAverages.wine1.acidity + wineAverages.wine2.acidity + wineAverages.wine3.acidity;
      
      if (totalAcidityRating > 0) {
        const wine1Percentage = Math.round((wineAverages.wine1.acidity / totalAcidityRating) * 100);
        const wine2Percentage = Math.round((wineAverages.wine2.acidity / totalAcidityRating) * 100);
        const wine3Percentage = Math.round((wineAverages.wine3.acidity / totalAcidityRating) * 100);
        
        // Round to nearest 5% intervals
        const roundedWine1 = roundToNearestInterval(wine1Percentage);
        const roundedWine2 = roundToNearestInterval(wine2Percentage);
        const roundedWine3 = roundToNearestInterval(wine3Percentage);
        
        // Normalize to ensure they add up to 100%
        const { wine1: finalWine1, wine2: finalWine2, wine3: finalWine3 } = normalizePercentages(roundedWine1, roundedWine2, roundedWine3);
        
        recommendations.push({
          name: 'High Acidity Blend',
          description: `${finalWine1}% Wine 1, ${finalWine2}% Wine 2, ${finalWine3}% Wine 3`,
          wine1: finalWine1,
          wine2: finalWine2,
          wine3: finalWine3
        });
      }
    }

    // Recommendation 4: Based on sweetness preference - properly weighted by user ratings
    if (sweetnessPreference === 'high') {
      const totalSweetnessRating = wineAverages.wine1.sweetness + wineAverages.wine2.sweetness + wineAverages.wine3.sweetness;
      
      if (totalSweetnessRating > 0) {
        const wine1Percentage = Math.round((wineAverages.wine1.sweetness / totalSweetnessRating) * 100);
        const wine2Percentage = Math.round((wineAverages.wine2.sweetness / totalSweetnessRating) * 100);
        const wine3Percentage = Math.round((wineAverages.wine3.sweetness / totalSweetnessRating) * 100);
        
        // Round to nearest 5% intervals
        const roundedWine1 = roundToNearestInterval(wine1Percentage);
        const roundedWine2 = roundToNearestInterval(wine2Percentage);
        const roundedWine3 = roundToNearestInterval(wine3Percentage);
        
        // Normalize to ensure they add up to 100%
        const { wine1: finalWine1, wine2: finalWine2, wine3: finalWine3 } = normalizePercentages(roundedWine1, roundedWine2, roundedWine3);
        
        recommendations.push({
          name: 'High Sweetness Blend',
          description: `${finalWine1}% Wine 1, ${finalWine2}% Wine 2, ${finalWine3}% Wine 3`,
          wine1: finalWine1,
          wine2: finalWine2,
          wine3: finalWine3
        });
      }
    }

    // Recommendation 5: Based on body preference - properly weighted by user ratings
    if (bodyPreference === 'full') {
      // Calculate weighted percentages based on actual body ratings
      const totalBodyRating = wineAverages.wine1.body + wineAverages.wine2.body + wineAverages.wine3.body;
      
      if (totalBodyRating > 0) {
        const wine1Percentage = Math.round((wineAverages.wine1.body / totalBodyRating) * 100);
        const wine2Percentage = Math.round((wineAverages.wine2.body / totalBodyRating) * 100);
        const wine3Percentage = Math.round((wineAverages.wine3.body / totalBodyRating) * 100);
        
        // Round to nearest 5% intervals
        const roundedWine1 = roundToNearestInterval(wine1Percentage);
        const roundedWine2 = roundToNearestInterval(wine2Percentage);
        const roundedWine3 = roundToNearestInterval(wine3Percentage);
        
        // Normalize to ensure they add up to 100%
        const { wine1: finalWine1, wine2: finalWine2, wine3: finalWine3 } = normalizePercentages(roundedWine1, roundedWine2, roundedWine3);
        
        recommendations.push({
          name: 'Full Body Blend',
          description: `${finalWine1}% Wine 1, ${finalWine2}% Wine 2, ${finalWine3}% Wine 3`,
          wine1: finalWine1,
          wine2: finalWine2,
          wine3: finalWine3
        });
      }
    }

    // Limit to 5 recommendations
    return recommendations.slice(0, 5);
  };

  const handleNextTrial = () => {
    if (currentTrial < guidedTrials.length - 1) {
      setCurrentTrial(currentTrial + 1);
    } else {
      setCurrentSection('recommended');
    }
  };

  const handleBackTrial = () => {
    if (currentTrial > 0) {
      setCurrentTrial(currentTrial - 1);
    } else {
      setCurrentSection('intro');
    }
  };

  if (currentSection === 'intro') {
    return (
      <div className={`blending-trials ${classVersion}-theme`}>
        <div className="trials-header">
          <button className="back-button" onClick={onBack}>
            ← Back to Navigation
          </button>
          <img src={logo} className="trials-logo" alt="TBL Logo" />
          <h1>Blending Trials</h1>
          <p className="trials-description">
            Experiment with different wine blends to discover your preferences
          </p>
        </div>

        <div className="trials-options">
          <div className="trial-card" onClick={() => setCurrentSection('guided')}>
            <div className="trial-icon">📋</div>
            <h3>Guided Blending — Starter Pack</h3>
            <p>Follow our recommended starter blends to learn the basics</p>
          </div>
          
          <div className="trial-card" onClick={() => setCurrentSection('recommended')}>
            <div className="trial-icon">💡</div>
            <h3>Recommended Blends</h3>
            <p>Try personalized blends based on your taste preferences</p>
          </div>
        </div>

        <div className="footer">
          <div className="footer-separator"></div>
          <img src={logo} className="footer-logo" alt="TBL Logo" />
        </div>
      </div>
    );
  }

  if (currentSection === 'guided') {
    const currentBlend = guidedTrials[currentTrial];
    
    return (
      <div className={`blending-trials ${classVersion}-theme`}>
        <div className="trials-header">
          <button className="back-button" onClick={handleBackTrial}>
            ← Back
          </button>
          <img src={logo} className="trials-logo" alt="TBL Logo" />
          <h1>Guided Blending — Starter Pack</h1>
          <p className="trials-description">
            Trial {currentTrial + 1} of {guidedTrials.length}: {currentBlend.name}
          </p>
        </div>

        <div className="blend-container">
          <div className="blend-card">
            <h2 className="blend-name">{currentBlend.name}</h2>
            <p className="blend-description">{currentBlend.description}</p>
            
            <div className="cylinder-size-selector">
              <h3>Select Graduated Cylinder Size:</h3>
              <div className="size-options">
                {cylinderSizes.map((size) => (
                  <button
                    key={size.value}
                    className={`size-button ${cylinderSize === size.value ? 'active' : ''}`}
                    onClick={() => setCylinderSize(size.value)}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="blend-ratios">
              <h3>Blend Ratios & Measurements:</h3>
              <div className="ratios-table">
                <table>
                  <thead>
                    <tr>
                      <th>Wine</th>
                      <th>Percentage</th>
                      <th>ML Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBlend.wine1 && (
                      <tr>
                        <td>Wine 1</td>
                        <td>{currentBlend.wine1}%</td>
                        <td>{getMLAmount(currentBlend.wine1, cylinderSize)}ML</td>
                      </tr>
                    )}
                    {currentBlend.wine2 && (
                      <tr>
                        <td>Wine 2</td>
                        <td>{currentBlend.wine2}%</td>
                        <td>{getMLAmount(currentBlend.wine2, cylinderSize)}ML</td>
                      </tr>
                    )}
                    {currentBlend.wine3 && (
                      <tr>
                        <td>Wine 3</td>
                        <td>{currentBlend.wine3}%</td>
                        <td>{getMLAmount(currentBlend.wine3, cylinderSize)}ML</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button className="next-button" onClick={handleNextTrial}>
            {currentTrial < guidedTrials.length - 1 ? 'Next Trial' : 'View Recommended Blends'}
          </button>
        </div>

        <div className="footer">
          <div className="footer-separator"></div>
          <img src={logo} className="footer-logo" alt="TBL Logo" />
        </div>
      </div>
    );
  }

  if (currentSection === 'recommended') {
    const recommendedBlends = generateRecommendedBlends();
    
    return (
      <div className={`blending-trials ${classVersion}-theme`}>
        <div className="trials-header">
          <button className="back-button" onClick={() => setCurrentSection('intro')}>
            ← Back to Options
          </button>
          <img src={logo} className="trials-logo" alt="TBL Logo" />
          <h1>Recommended Blends</h1>
          <p className="trials-description">
            Personalized blend recommendations based on your taste preferences and wine ratings
          </p>
        </div>

        <div className="recommended-container">
          {!wineRatings || !quizAnswers ? (
            <div className="recommended-card">
              <h2>Complete Previous Modules</h2>
              <p>Please complete the "Try Each Wine" and "Understand Your Preferences" modules first to get personalized blend recommendations.</p>
            </div>
          ) : recommendedBlends.length === 0 ? (
            <div className="recommended-card">
              <h2>No Recommendations Available</h2>
              <p>Unable to generate recommendations with the current data. Please ensure you've completed all rating modules.</p>
            </div>
          ) : (
            <div className="recommendations-grid">
              {recommendedBlends.map((blend, index) => (
                <div key={index} className="recommendation-card">
                  <h3>{blend.name}</h3>
                  <p className="blend-description">{blend.description}</p>
                  
                  <div className="cylinder-size-selector">
                    <h4>Select Graduated Cylinder Size:</h4>
                    <div className="size-options">
                      {cylinderSizes.map((size) => (
                        <button
                          key={size.value}
                          className={`size-button ${cylinderSize === size.value ? 'active' : ''}`}
                          onClick={() => setCylinderSize(size.value)}
                        >
                          {size.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="blend-ratios">
                    <h4>Blend Ratios & Measurements:</h4>
                    <div className="ratios-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Wine</th>
                            <th>Percentage</th>
                            <th>ML Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {blend.wine1 && (
                            <tr>
                              <td>Wine 1</td>
                              <td>{blend.wine1}%</td>
                              <td>{getMLAmount(blend.wine1, cylinderSize)}ML</td>
                            </tr>
                          )}
                          {blend.wine2 && (
                            <tr>
                              <td>Wine 2</td>
                              <td>{blend.wine2}%</td>
                              <td>{getMLAmount(blend.wine2, cylinderSize)}ML</td>
                            </tr>
                          )}
                          {blend.wine3 && (
                            <tr>
                              <td>Wine 3</td>
                              <td>{blend.wine3}%</td>
                              <td>{getMLAmount(blend.wine3, cylinderSize)}ML</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="action-buttons">
          <button className="next-button" onClick={onNext}>
            Continue to Next Section
          </button>
        </div>

        <div className="footer">
          <div className="footer-separator"></div>
          <img src={logo} className="footer-logo" alt="TBL Logo" />
        </div>
      </div>
    );
  }

  return null;
};

export default BlendingTrials;
